import {
  ACCOUNT_CLIENT_ID,
  ACCOUNT_DEMO_URL,
  ACCOUNT_DEV_URL,
  ACCOUNT_TEST_URL,
  APPLINKS_DEMO_URL,
  APPLINKS_DEV_URL,
  APPLINKS_TEST_URL,
} from "@eventr/common";
import { Capacitor } from "@capacitor/core";
import { reactAppStage, type ReactAppStage } from "../../utils/reactAppStage";

/**
 * Where Keycloak sends the browser back after an email-link or OIDC sign-in, and the value
 * the token exchange in oauth.slice.ts has to repeat verbatim.
 *
 * A native build has no origin to come back to, so it hands the callback to applinks, whose
 * page deep-links into the app. Anything running in a browser finishes the code exchange
 * itself on /oauth/callback, so it has to name its own origin: sending a browser user to
 * applinks bounces them at the app-store page and the sign-in never completes.
 *
 * The origin is read at runtime rather than baked in per stage, so one rule covers the dev
 * server on whatever port it landed on, the guest preview on pr.<stage>, the hosted web app on
 * team.<stage>.eventr.softpauer.com, and the teamsApp container on localhost:4303.
 *
 * Every origin the app is served from therefore needs a matching redirect URI — and, because
 * these calls are cross-origin, a matching web origin — on the `eventrApp` Keycloak client.
 * Register them as wildcards (`http://localhost:3000/*`), since the `?stage=` suffix below is
 * part of the URI Keycloak matches.
 */
const redirectUri = (nativeUri: string, stage: ReactAppStage) => {
  if (Capacitor.isNativePlatform()) return nativeUri;
  return `${window.location.origin}/oauth/callback?stage=${stage}`;
};

export const oidcConfig = () => {
  switch (reactAppStage()) {
    case "dev":
      return {
        authority: ACCOUNT_DEV_URL,
        client_id: ACCOUNT_CLIENT_ID,
        redirect_uri: redirectUri(`${APPLINKS_DEV_URL}/oauth/callback?stage=dev`, "dev"),
        client_secret: "st2eLPCcwVCoKuQBWWRuM8mpcAVPFGe1",
      };
    case "test":
      return {
        authority: ACCOUNT_TEST_URL,
        client_id: ACCOUNT_CLIENT_ID,
        redirect_uri: redirectUri(`${APPLINKS_TEST_URL}/oauth/callback?stage=test`, "test"),
        client_secret: "E0tlEi0NITMVN8mZvmsPy9reNtwNKPOz",
      };
    case "demo":
      return {
        authority: ACCOUNT_DEMO_URL,
        client_id: ACCOUNT_CLIENT_ID,
        redirect_uri: redirectUri(`${APPLINKS_DEMO_URL}/oauth/callback?stage=demo`, "demo"),
        client_secret: "phZLEbaWiiaXXugAdoEqZb3T4UZ4xNyn",
      };
    case "local":
      return {
        authority: "http://localhost:4200/",
        client_id: ACCOUNT_CLIENT_ID,
        redirect_uri: redirectUri("http://localhost:3000/oauth/callback", "local"),
        client_secret: "vIse7jPonTalW9UVktJmkBZkLML1wFOs",
      };
  }
};
